/**
 * Who can see what.
 *
 * §14.3 requires "a permanent 'who can see what' dashboard". Permanent is the
 * operative word: this is not a screen shown once during onboarding and then
 * buried, it is a standing answer the patient can come back to.
 *
 * READ ONLY in this slice. §14.3 also requires one-tap revocation, and that is
 * missing — deliberately, and said so on the page rather than left for the
 * patient to discover by hunting for a button. A revocation is a write from a
 * patient identity, and it needs the consent ledger's event trail and its own
 * audit story before it can be trusted; §14.1 makes every grant, modification
 * and revocation an immutable ledger entry, not a row update.
 */

import { type JSX } from 'react';

import { Async } from '../components/Async.js';
import { useConsents } from '../lib/queries.js';

const when = (epochMs: number | null): string =>
  epochMs === null ? 'no end date' : new Date(epochMs).toISOString().slice(0, 10);

export const WhoCanSee = ({ patientId }: { readonly patientId: string }): JSX.Element => {
  const consents = useConsents(patientId);

  return (
    <section aria-labelledby="consent-heading">
      <h1 id="consent-heading">Who can see what</h1>

      <p>
        These are the permissions on your record. Each one says who is covered, what they may do,
        and why they may do it.
      </p>

      <Async query={consents} empty="There are no permissions recorded on your record.">
        {(grants) => (
          <table data-testid="consent-dashboard">
            <caption>{grants.length} permission(s)</caption>
            <thead>
              <tr>
                <th scope="col">Who</th>
                <th scope="col">May</th>
                <th scope="col">For</th>
                <th scope="col">Until</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((grant) => (
                <tr key={grant.id}>
                  <td>
                    {grant.recipientRef ??
                      grant.actorRef ??
                      `Anyone acting as ${grant.actorType ?? 'unknown'}`}
                  </td>
                  <td>{grant.permittedActions.join(', ')}</td>
                  <td>{grant.permittedPurposes.join(', ')}</td>
                  <td>{when(grant.endEpochMs)}</td>
                  <td>{grant.status}</td>
                </tr>
              ))}
              {/*
                Withheld categories are shown when present. §13 tags sensitive
                data and lets a consent exclude it — a patient who has withheld
                a category should be able to see that they did.
              */}
            </tbody>
          </table>
        )}
      </Async>

      {(consents.data ?? []).some((grant) => grant.withheldSensitivities.length > 0) && (
        <section aria-labelledby="withheld-heading">
          <h2 id="withheld-heading">Kept private</h2>
          <ul>
            {(consents.data ?? []).flatMap((grant) =>
              grant.withheldSensitivities.map((category) => (
                <li key={`${grant.id}-${category}`}>
                  {category} — not shared under permission {grant.id}
                </li>
              )),
            )}
          </ul>
        </section>
      )}

      <p className="notice" role="note">
        You cannot change these here yet. To grant or withdraw a permission, contact your care team
        — they will record it, and it will appear on this page.
      </p>
    </section>
  );
};
